import { selectIsFeatureEnabled } from 'tradera/state/environment/selectors';
import {
    getShippingProvidersByWeight,
    getShippingRecommendations
} from 'tradera/apps/syi/services/shipping-provider-service';
import {
    getSyiSlice,
    selectItemIdFromDraft
} from 'tradera/apps/syi/state/selectors/syi';

import { logger } from 'packages/logger';
import {
    selectFirstRecommendedByWeight,
    selectSelectedShippingOptions
} from '../reducers/shipping/shipping-selectors';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { isDenmarkCountryCode } from 'tradera/localization/countrycode';
import { trackSyiForm } from 'tradera/apps/syi/services/syi-ga4-service';
import {
    clearShippingRecommendations,
    setShippingOptionsByWeight,
    setShippingOptionsError,
    setTakeawaySelected,
    setShippingRecommendations as setShippingRecommendationsReducer,
    setShippingOptionSelected,
    setShippingOptionsLoading
} from 'tradera/apps/syi/state/reducers/shipping/shipping-reducer';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';
import type { ShippingOption } from 'tradera/state/services/types/webapi-discover-generated';
import {
    selectDraftId,
    selectMagicalSyiSettngEnabled
} from 'tradera/apps/syi/state/selectors/draft-selectors';
import { setHasUsedAutoFill } from 'tradera/apps/syi/state/reducers/predictions-reducer';
import { preferredShippingProvidersApi } from 'tradera/components/shipping/preferred-shipping-providers/preferred-shipping-providers-api';
import { isNullOrUndefined } from 'tradera/utils/condition-check';

export const setOfferTakeaway =
    (offerTakeaway: boolean) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const draftId = selectDraftId(getState());

        trackSyiForm({
            eventName: 'syi_pickup',
            parameters: {
                boolean_value: offerTakeaway ? 1 : 0,
                draft_id: draftId
            }
        });
        dispatch(setTakeawaySelected({ offerTakeaway }));
    };

export const getAndSetShippingOptionsByWeight =
    (shippingOptions?: ShippingOption[]) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        try {
            const { charity, shipping } = getSyiSlice(getState());
            const itemId = selectItemIdFromDraft(getState());
            const { charityId } = charity;
            let fromCountry: string | null =
                selectShippingRegionCountryCodeIso2(getState());
            if (isDenmarkCountryCode(fromCountry)) {
                fromCountry = shipping.fromCountry;
            }

            dispatch(setShippingOptionsLoading(true));

            const { data } = await getShippingProvidersByWeight(
                fromCountry,
                charityId,
                itemId
            );
            const { productsPerWeightSpan } = data;

            dispatch(
                setShippingOptionsByWeight({
                    productsPerWeightSpan,
                    shippingOptions:
                        shippingOptions ||
                        selectSelectedShippingOptions(getState())
                })
            );
        } catch (error) {
            dispatch(setShippingOptionsError());
        }
    };

export const setShippingRecommendations =
    (categoryId: number) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const isShippingRecommendationsEnabled = selectIsFeatureEnabled(
            'shipping-recommendations'
        )(getState());

        if (!isShippingRecommendationsEnabled || !categoryId) {
            return;
        }

        const isTShirtSizesEnabled = selectIsFeatureEnabled(
            'tshirt-sizes-shipping-selector'
        )(getState());

        // If t-shirt sizes is enabled, we want to show onboarding if user
        // hasn't selected shipping providers - and onboarding won't show
        // if user has selected shipping providers.
        if (isTShirtSizesEnabled) {
            const { data } = await dispatch(
                preferredShippingProvidersApi.endpoints.getSelectedShippingProviders.initiate()
            );
            if (isNullOrUndefined(data?.selectedShippingProviders)) {
                return;
            }
        }

        try {
            const { categoryShippingRecommendations } =
                await getShippingRecommendations(categoryId);
            const { recommendedWeight, shippingRecommendations } =
                categoryShippingRecommendations;
            dispatch(
                setShippingRecommendationsReducer({
                    recommendedWeight,
                    shippingRecommendations
                })
            );
            dispatch(setFirstRecommendationAsSelected());
        } catch (error) {
            dispatch(clearShippingRecommendations());
            logger(
                'Get ShippingRecommendations failed. Reason: ' +
                    (typeof error === 'object' && error && 'message' in error
                        ? error.message
                        : '')
            );
        }
    };

export const setFirstRecommendationAsSelected =
    () => (dispatch: AppDispatch, getState: GetState) => {
        const state = getState();
        const hasUserAutofillEnabledInSettings =
            selectMagicalSyiSettngEnabled(state);

        const alreadySelectedShippingOptions =
            selectSelectedShippingOptions(state);

        const preFillShippingEnabled = selectIsFeatureEnabled(
            'magical-syi-autofill-shipping'
        )(state);

        if (
            alreadySelectedShippingOptions.length > 0 ||
            !hasUserAutofillEnabledInSettings ||
            !preFillShippingEnabled
        ) {
            return;
        }

        const firstRecommendation = selectFirstRecommendedByWeight(state);

        if (firstRecommendation) {
            dispatch(setShippingOptionSelected([firstRecommendation]));
            dispatch(setHasUsedAutoFill(true));
        }
    };
